import { cva } from 'class-variance-authority';

/**
 * Base button styles applied to all button variants and sizes.
 */
export const baseButtonStyles = [
  'border-0',
  'data-[state=isTransitioning]:pointer-events-none',
  'disabled:cursor-not-allowed',
  'duration-200',
  'ease-in-out',
  'focus-visible:outline-1',
  'focus-visible:outline-brand-primary-700',
  'focus-visible:outline-dashed',
  'focus-visible:outline-offset-2',
  'font-bold',
  'gap-4',
  'inline-flex',
  'items-center',
  'justify-center',
  'print:hidden',
  'rounded-full',
  'transition-colors',
  'w-auto',
  'whitespace-nowrap',
].join(' ');

/**
 * Button variants using class-variance-authority.
 * This setup allows for easy extension and maintenance of button styles.
 * Each variant and size can be customized with specific styles,
 * and compound variants handle specific state combinations.
 */
export const buttonVariants = cva(baseButtonStyles, {
  variants: {
    variant: {
      // Primary button - Red background
      primary: ['bg-brand-primary', 'text-neutral-white'],

      // Secondary button - Red outline
      secondary: ['bg-transparent', 'ring-2 ring-inset ring-brand-primary', 'text-brand-primary'],

      // Tertiary button - Text only
      tertiary: ['bg-transparent', 'text-brand-primary'],

      // Tertiary Two-Tone button - Black text with primary icon color
      tertiaryTwoTone: ['bg-transparent', 'text-neutral-black'],

      // Link button - Text only without special focus ring styles
      link: [
        '!font-normal',
        'bg-transparent',
        'decoration-auto',
        'decoration-inherit',
        'decoration-solid',
        'text-neutral-black',
        'underline-offset-4',
        'underline',
      ],
    },

    size: {
      // Medium size (default) - Desktop/Tablet button size
      medium: ['px-8 py-3', 'min-h-14 max-h-14', 'typography-button'],

      // Small size (compact) - Button small size
      small: ['gap-2', 'px-5 py-0.5', 'min-h-8 max-h-8', 'typography-button-small'],
    },

    // Icon-only button modifier
    iconOnly: {
      true: 'p-0 rounded-full',
      false: '',
    },
  },

  // Compound variants for different states and variants
  compoundVariants: [
    // Primary button styles
    {
      variant: 'primary',
      class: [
        'active:bg-brand-primary-700',
        'disabled:bg-semantic-buttons-inactive-background',
        'disabled:text-semantic-buttons-inactive-text',
        'focus-visible:bg-brand-primary-700',
        'hover:bg-brand-primary-600',
      ],
    },

    // Secondary button styles
    {
      variant: 'secondary',
      class: [
        'active:bg-accent-surface-red',
        'active:ring-brand-primary-700',
        'active:text-brand-primary-700',
        'disabled:bg-semantic-buttons-inactive-background',
        'disabled:ring-neutral-gray-500',
        'disabled:text-semantic-buttons-inactive-text',
        'focus-visible:bg-accent-surface-red',
        'focus-visible:ring-brand-primary-700',
        'focus-visible:text-brand-primary-700',
        'hover:ring-brand-primary-600',
        'hover:text-brand-primary-600',
      ],
    },

    // Tertiary button styles
    {
      variant: 'tertiary',
      class: [
        'active:bg-accent-surface-red',
        'active:ring-brand-primary-700',
        'active:text-brand-primary-700',
        'disabled:bg-transparent',
        'disabled:text-semantic-buttons-inactive-text',
        'focus-visible:bg-accent-surface-red',
        'focus-visible:ring-brand-primary-700',
        'focus-visible:text-brand-primary-700',
        'hover:ring-brand-primary-600',
        'hover:text-brand-primary-600',
        'px-2 py-1',
      ],
    },

    // Tertiary Two-Tone button styles
    {
      variant: 'tertiaryTwoTone',
      class: [
        'active:bg-accent-surface-red',
        'active:ring-brand-primary-700',
        'active:text-brand-primary-700',
        'disabled:bg-transparent',
        'disabled:text-semantic-buttons-inactive-text',
        'focus-visible:bg-accent-surface-red',
        'focus-visible:ring-brand-primary-700',
        'focus-visible:text-brand-primary-700',
        'hover:ring-brand-primary-600',
        'hover:text-brand-primary-600',
        'px-2 py-1',
      ],
    },

    // Link button styles
    {
      variant: 'link',
      class: [
        'active:text-brand-primary',
        'disabled:bg-transparent',
        'disabled:text-semantic-buttons-inactive-text',
        'focus-visible:outline-brand-primary',
        'focus-visible:text-brand-primary',
        'hover:text-brand-primary',
        'px-1 py-0',
        'rounded-sm',
      ],
    },

    // Tertiary medium button styles
    {
      variant: 'tertiary',
      size: 'medium',
      class: ['min-h-10 max-h-10'],
    },

    // Tertiary Two-Tone medium button styles
    {
      variant: 'tertiaryTwoTone',
      size: 'medium',
      class: ['min-h-10 max-h-10'],
    },

    // Tertiary small button styles
    {
      variant: 'tertiary',
      size: 'small',
      class: ['px-2 py-0.5'],
    },

    // Tertiary Two-Tone small button styles
    {
      variant: 'tertiaryTwoTone',
      size: 'small',
      class: ['px-2 py-0.5'],
    },

    // Link medium button styles
    {
      variant: 'link',
      size: 'medium',
      class: ['px-1 py-0', 'min-h-none'],
    },
    // Link small button styles
    {
      variant: 'link',
      size: 'small',
      class: ['px-1 py-0', 'min-h-none'],
    },

    // Icon-only small button
    {
      size: 'small',
      iconOnly: true,
      class: 'min-h-8 min-w-8',
    },

    // Icon-only medium button
    {
      size: 'medium',
      iconOnly: true,
      class: 'h-14 w-14',
    },
  ],

  // Default button variant outputs
  defaultVariants: {
    variant: 'primary',
    size: 'medium',
    iconOnly: false,
  },
});
